import React, { ChangeEventHandler, useCallback, useEffect, useMemo, useState, lazy, Suspense, useRef } from "react";
import { matchPath, Redirect, useHistory, useParams } from "react-router";
import classNames from "classnames";

import { EditorSizes, TemplateNames } from "../../../types/template-layouts";
import {
	ActionSource,
	BlProfile,
	BrandliveEvent,
	LanguagesAbbr,
	ModuleGroups,
	PageModuleGroupModules,
	PageModuleType,
	RegistrationStepType,
	Session,
	SessionTabsLayoutTypes,
	SessionTypesEnum,
	TicketingSet,
	TranslateString,
	IAnnouncement,
	EPreviewTypes,
	EBroadcastTypes,
	ModuleMap,
	SqsSocketTypes,
} from "../../../types/working-model";
import Icon from "../../general-ui/icon";
import { ParamsProps } from '../live-event';
import {
	clearErrorBoundaryComponents,
	getUserSession,
	setUserSession,
	getSessionUserSurveyRating,
	setSessionUserSurveyRating,
	resetUserSession
} from "../../../store/actions/event/event-actions";
import { getStorageItem } from "../../../utils/local-storage";
import SessionHeader from "./session-modules/header/header";
import { getMockBlProfile, getStoredReferrer, getTemplateClassName, isMobile, replaceSpaceWithDash } from "../../../utils/utils";
import { eventPath, getSessionStylingOverrides } from "../utils";
import { useScreenMediaQuery } from "../../../utils/use-screen-media-query";
import SessionDocumentHead from "./session-document-head";
import MarketingNav from "../marketing-page/navigation/marketing-nav";
import useTranslationModules, { useTranslate } from "../../../i18n/useTranslationModules";
import { hasEventAccess, hasSessionAccess } from "../../../utils/registration-utils";
import { useSocket } from '../../../connection/socket';
import { showAnnouncement } from "../../general-ui/alert/alert-service";
import { useAppDispatch, useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { useTracking } from "../../../utils/tracking";
import SessionStream from './session-stream/session-stream';
import SessionDescription from './session-modules/description/description';
import MainEventBannerBreakout from '../modules/main-event-banner-breakout/main-event-banner-breakout';
import ErrorBoundary, { TEST_ERROR_BOUNDARIES } from "../../../utils/error-boundary";
import useRouteMap, { DebugDefaultRouteMap, DefaultRouteMap } from "../hooks/use-route-map";
import useTranslationRoute from "../hooks/use-translation-route";
import { LanguageDropdown } from "../../general-ui/language-dropdown/language-dropdown";
import { OptionalComponent } from "../../../utils/optional-component";
import useChangeLanguage from "../../../utils/use-change-language";
import useTrackEndSessionTimes from "../../../utils/tracking/use-track-end-session-times";
import FeedbackSurveyAlert from '../../general-ui/user-survey-modal/user-survey-alert';
import socketManager from "../../../connection/socket-main-thread/socket-manager";
import { loadChatMessagesEffect } from "./live-chat/chat-loader";
import useTrackPageView from "../../../utils/tracking/use-track-page-view";

const SessionBreakoutRooms = lazy(() => import("./session-modules/breakout-rooms/breakout-rooms"));
const SessionPreviewModal = lazy(() => import("../../general-ui/session-preview/session-preview"));
const ProfileDetailsDropdown = lazy(() => import('../marketing-page/navigation/profile-details-dropdown'));
const FiresideHostControls = lazy(() => import("./fireside/session-host"));
const SessionDocuments = lazy(() => import("./session-modules/documents/documents"));
const Sticky = lazy(() => import("../../general-ui/sticky/sticky"));
const SessionSpeakers = lazy(() => import("./session-modules/speakers/speakers"));
const SessionSimilarSessions = lazy(() => import("./session-modules/similar-sessions/similar-sessions"));
const SessionProducts = lazy(() => import("./session-modules/products/products"));
const SessionSurvey = lazy(() => import("./session-modules/survey/survey"));
const SessionQuestions = lazy(() => import("./session-modules/questions/questions"));
const LiveChat = lazy(() => import("./live-chat/live-chat"));
const BlankSection = lazy(() => import("../modules/blank-section/blank-section"));
const Footer = lazy(() => import("../marketing-page/footer/footer"));
const UserSurveyModal = lazy(() => import("../../general-ui/user-survey-modal/user-survey-modal"));

import '../../../scss/live-event/base/session/session.scss';


export enum ETabLayoutClassNames {
	noTabs = 'no-tabs',
	vertical = 'vertical',
	horizontal = 'horizontal'
}

interface RenderSessionModulesProps {
	session: Session;
	template: string;
	moduleGroup?: PageModuleGroupModules;
	preview?: boolean;
}
const RenderSessionModules: React.FC<RenderSessionModulesProps> = ({ session, template, moduleGroup, preview }) => {
	const modules = useMemo(() => {
		const tabLayoutClassname = replaceSpaceWithDash(session.layout?.tabs?.layout.toLowerCase() || ETabLayoutClassNames.horizontal);

		// Breakout Rooms is separate because it's not a normal module group that has child modules inside
		if (moduleGroup?.type === "breakout" && moduleGroup.is_on) {
			return <Suspense fallback="">
				<ErrorBoundary uniqueLabel="Session breakout rooms">
					<SessionBreakoutRooms key={module.id} session={session} template={template} />
				</ErrorBoundary>
			</Suspense>;
		}

		return (
			<Suspense fallback="">
				<>
					{moduleGroup?.modules.map(moduleId => {
						const module = session?.modules.find(module => module.id === moduleId);
						if (!module || !module.is_on || !moduleGroup.is_on || (module.modules && module.modules.length === 0)) return null;
						let moduleToRender = <></>;

						switch (module.type) {
							case (PageModuleType.description): {
								moduleToRender = <SessionDescription key={module.id} module={module} template={template} session={session} />;
								break;
							}
							case (PageModuleType.speakers): {
								moduleToRender = <SessionSpeakers key={module.id} module={module} template={template} tabLayout={tabLayoutClassname as ETabLayoutClassNames} />;
								break;
							}
							case (PageModuleType.similar_sessions): {
								moduleToRender = <SessionSimilarSessions key={module.id} module={module} template={template} />;
								break;
							}
							case (PageModuleType.products): {
								moduleToRender = <SessionProducts key={module.id} module={module} template={template} tabLayout={tabLayoutClassname as ETabLayoutClassNames} />;
								break;
							}
							case (PageModuleType.quizzes): {
								// FUTURE DEV - hiding quizzes and posts
								// return <SessionQuizzes key={module.id} module={module} template={template} />;
								return;
							}
							case (PageModuleType.survey): {
								moduleToRender = <SessionSurvey key={module.id} module={module} template={template} sessionUuid={session.uuid} preview={preview} />;
								break;
							}
							case (PageModuleType.documents): {
								moduleToRender = <SessionDocuments key={module.id} module={module} template={template} />;
								break;
							}
							case (PageModuleType.questions): {
								moduleToRender = <SessionQuestions key={module.id} module={module} template={template} session={session.session} session_uuid={session.uuid} />;
								break;
							}
							case (PageModuleType.embed_widget):
							case (PageModuleType.blank): {
								moduleToRender = <BlankSection key={module.id} module={module} template={template} />;
								break;
							}
						}
						return (
							<ErrorBoundary uniqueLabel={`${ModuleMap[module.type]} ${module.id?.toString()}`} key={module.id}>
								{moduleToRender}
							</ErrorBoundary>
						);
					})}
				</>
			</Suspense>);

	}, [session, moduleGroup, template, preview]);

	return <>{modules}</>;
};

interface SessionViewProps {
	singleSession?: boolean;
	previewBundle?: BrandliveEvent;
	previewSession?: Session;
	previewLanguage?: LanguagesAbbr;
	interacted?: boolean;
	debug?: boolean;
}

const SessionView: React.FC<SessionViewProps> = ({ singleSession, previewBundle, previewSession, previewLanguage, debug }) => {
	const loadingEventBundle = useTypedSelector(state => state.LiveEventReducer.loadingEventBundle);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const gettingUserSession = useTypedSelector(state => state.LiveEventReducer.gettingUserSession);
	const blProfileUserToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const playbackUrls = useTypedSelector(state => state.LiveEventReducer.playbackUrls);
	const secondaryVideos = useTypedSelector(state => state.LiveEventReducer.secondaryVideos);
	const getUserSessionError = useTypedSelector(state => state.LiveEventReducer.getUserSessionError);
	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const registrationId = useTypedSelector(state => state.LiveEventReducer.registrationId);
	const validSessions = useTypedSelector(state => state.LiveEventReducer.validSessions);
	const finishedRegistering = useTypedSelector(state => state.LiveEventReducer.finishedRegistering);
	const userVerified = useTypedSelector(state => state.LiveEventReducer.userVerified);
	const firesidesHostSessions = useTypedSelector(state => state.LiveEventReducer.firesidesHostSessions);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const validPasscodeLists = useTypedSelector((state) => state?.LiveEventReducer?.validPasscodeLists);
	const loadingValidSessions = useTypedSelector(event => event.LiveEventReducer.loadingValidSessions);
	const sessionUserSurveyRatings = useTypedSelector(state => state.LiveEventReducer.sessionFeedbackSurveyRatings);
	const paidSessions = useTypedSelector(state => state.LiveEventReducer.paidSessions);

	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const adminUser = useTypedSelector(state => state.AuthReducer.user);
	const attendeeInPersonModeEnabled = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings.attendeeInPersonEnabled);
	const registeredLanguage = useTypedSelector(state => state.LiveEventReducer?.registeredLanguage);

	const [selectedTabUuid, setSelectedTabUuid] = useState<string>();
	const [backupUrlEnabled, setBackupUrlEnabled] = useState<boolean>(false);
	const [backupUrl, setBackupUrl] = useState<string>('');
	const [sessionStarted, setSessionStarted] = useState(false);
	const [showFeedbackSurveyAlert, setShowFeedbackSurveyAlert] = useState(true);
	const [surveyId, setSurveyId] = useState<number | null>(null);
	const [showFeedbackSurveyModal, setShowFeedbackSurveyModal] = useState(false);

	const translationUpdates = useTypedSelector(state => state.LiveEventReducer.translationUpdates);

	const sessionHeaderRef = useRef<HTMLDivElement | null>(null);

	const dispatch = useAppDispatch();
	const RouteMap = useRouteMap({ debug });
	const history = useHistory();

	const { sessionUuid: sessionParamUuid, uuid, eventName, language }: ParamsProps = useParams();

	const handleLanguageChange = useChangeLanguage(eventBundle);
	const {
		isLessThan640,
		isLessThan768,
		isLessThan1024
	} = useScreenMediaQuery();

	const isPreview = !!(previewBundle || previewSession);
	const event = isPreview ? previewBundle : eventBundle;
	const displayFooter = event?.settings?.display_session_footer ?? false;

	const sessionUuid = singleSession ? event?.sessions?.[0]?.uuid : sessionParamUuid;

	const setFeedbackRating = (rating: number) => sessionUuid && dispatch(setSessionUserSurveyRating(sessionUuid, rating));

	const previewLessThan1024 = (editorSize === 'tablet') || (editorSize === 'mobile');

	const session = useMemo(() => {
		if (singleSession) {
			return previewBundle?.sessions?.[0] ? previewBundle?.sessions?.[0] : eventBundle?.sessions?.[0];
		}
		if (previewSession) {
			return previewSession;
		}
		return eventBundle?.sessions?.find(session => session.uuid === sessionUuid);
	}, [singleSession, eventBundle, previewBundle, sessionUuid, previewSession]);

	useEffect(() => {
		if (!sessionStarted) {
			const pastStartTime = session?.timestamp
				? session.timestamp - Date.now() < 0
				: true; // null timestamp for on demand, so it's always past the start time

			if (pastStartTime) {
				setSessionStarted(true);
			}
		}
	}, [eventBundle, session, sessionStarted]);

	if (!session && !singleSession) {
		history.replace(`/${eventPath(eventName, uuid)}`);
	}
	const isFireside = session?.session_type === SessionTypesEnum.fireside;
	const isFiresideTablet = isFireside && isLessThan1024;
	// isMobile or isLessThan1024 (because "tablet" is considered less than 1024 in our chat layout)
	const [chatClosed, setChatClosed] = useState(isMobile || isLessThan640 || isFiresideTablet);

	const displaySessionPageLanguageToggle = (session?.languages ?? []).length > 1 && (event?.settings?.display_session_language_dropdown ?? false);
	const single_stream = session?.streaming_options?.single_stream ?? false; // defaulting to false for backwards compatibility

	const { backgroundColor, backgroundImg } = getSessionStylingOverrides(session?.layout.styling_overrides);

	const logo = isPreview ? workingEvent?.homepage?.header.logo : eventBundle?.homepage?.header.logo;

	const notValidTranslation = !session?.languages.includes(language);

	useTranslationModules({ language: notValidTranslation ? session?.default_language : language });

	useEffect(() => {
		if (sessionUuid && blProfileUser?.uuid && !sessionUserSurveyRatings[sessionUuid] && blProfileUserToken) {
			dispatch(getSessionUserSurveyRating(sessionUuid, blProfileUser.uuid, blProfileUserToken));
		}
		// do not add sessionUserSurveyRatings or we'll get an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionUuid, blProfileUser, blProfileUserToken, dispatch]);

	useEffect(() => {
		//check to make sure language is in translated list, if not, push them to the default language
		if (notValidTranslation && session && !isPreview) {
			if (singleSession) {
				history.replace(`/${eventPath(eventName, uuid)}/${session.default_language}`);
			} else {
				history.replace(`/${eventPath(eventName, uuid)}/${session.default_language}/session/${sessionUuid}`);
			}
		}
	}, [history, eventName, uuid, sessionUuid, session, notValidTranslation, isPreview, singleSession]);
	const translationNamespace = useTranslationRoute();
	const { t } = useTranslate(["session", translationNamespace]);

	// using socket for announcements and user surveys from admin or moderator
	const socket = useSocket(`session-${session?.uuid}-${language || 'en'}`);
	const sessionBackupSocket = socketManager.get(`event-${event?.uuid}`);

	const isRegistered = eventBundle?.registration_on && blProfileUserToken;
	const registeredSocket = useSocket(isRegistered ? `registered-${eventBundle?.uuid}-${language || 'en'}` : '');

	useEffect(() => {
		// show announcement as alert
		const handleAnnouncement = ({ data }: { data: Partial<IAnnouncement>; }) => {
			showAnnouncement({ ...data });
		};
		// show user survey modal
		const handleUserSurvey = ({ data }: { data: number; }) => {
			setSurveyId(data);
		};

		const handleSessionBackupUrl = ({ data }: { data: { backup_url_enabled: boolean, backup_url: string } }) => {
			setBackupUrlEnabled(data.backup_url_enabled);
			setBackupUrl(data.backup_url);
		};

		// add listeners and remove when done
		socket.addListener('announcement', handleAnnouncement);

		if (isRegistered) {
			registeredSocket.addListener('announcement', handleAnnouncement);
		}

		socket.addListener(SqsSocketTypes.SURVEY, handleUserSurvey);
		sessionBackupSocket.addListener('session-backup-url-update', handleSessionBackupUrl);
		return () => {
			socket.removeListener('announcement', handleAnnouncement);
			socket.removeListener(SqsSocketTypes.SURVEY, handleUserSurvey);
			registeredSocket?.removeListener?.('announcement', handleAnnouncement);
			sessionBackupSocket.removeListener('session-backup-url-update', handleSessionBackupUrl);
		};
	}, [socket, registeredSocket, sessionBackupSocket, isRegistered]);

	useEffect(() => {
		// set default tab to the first group
		if (session) {
			const firstSelected = session.module_grouping?.find(module => module.is_on);
			setSelectedTabUuid(firstSelected?.uuid);
		}
	}, [session]);

	useEffect(() => {
		if (TEST_ERROR_BOUNDARIES) {
			return () => {
				dispatch(clearErrorBoundaryComponents());
			};
		}
	}, [dispatch]);

	useEffect(() => {
		setBackupUrlEnabled(session?.backup_url_enabled || false);
		setBackupUrl(session?.backup_url || '');
	}, [session?.backup_url_enabled, session?.backup_url]);

	useEffect(() => {
		if (backupUrlEnabled && backupUrl && matchPath(location.pathname, { path: RouteMap.Session })) {
			window.location.href = backupUrl;
		}
	}, [backupUrlEnabled, backupUrl, RouteMap.Session]);

	const channel = eventBundle?.channel;
	const ticketingStep = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.ticketing && step.isOn);
	const hasTicket = hasSessionAccess(event, session, validSessions, paidSessions);


	// only fire effect on changes to this primitive value, not the entire session object
	const safeSingleSessionUuid = session?.uuid;

	//loading chat messages in this component rather than in the chat component
	//because that component moves around in the DOM due to the designs
	useEffect(() =>
		loadChatMessagesEffect(dispatch, safeSingleSessionUuid, isPreview, language),
		[safeSingleSessionUuid, language, dispatch, isPreview]);

	const layout: SessionTabsLayoutTypes = useMemo(() => {
		const _layout = session?.layout?.tabs?.layout || previewSession?.layout?.tabs?.layout || SessionTabsLayoutTypes.Horizontal;
		if (_layout === SessionTabsLayoutTypes.Vertical && isLessThan768) {
			return SessionTabsLayoutTypes.Horizontal;
		}
		return _layout;
	}, [isLessThan768, previewSession?.layout?.tabs?.layout, session?.layout?.tabs?.layout]);

	// Removes spaces for use in className: 'no-tabs','horizontal','vertical'
	const layoutClassName = layout?.toLowerCase().replace(" ", "-");

	const showIcons = session?.layout?.tabs?.show_icons ?? false;

	//DEV NOTE: to change a template and get the fonts and color palettes to 
	//load properly you must go back to the edotir and publish a new event with the desired theme settings
	const template = eventBundle?.template.name || previewBundle?.template.name || TemplateNames.Classic;

	const handleTabChange: ChangeEventHandler<HTMLInputElement> = useCallback(({ target }) => {
		if (TEST_ERROR_BOUNDARIES) dispatch(clearErrorBoundaryComponents());
		setSelectedTabUuid(target.value);
	}, [dispatch]);

	const sessionId = session?.session;
	const eventId = eventBundle?.event;
	const eventUuid = eventBundle?.uuid;
	const registrationOn = eventBundle?.registration_on;

	useEffect(() => {
		// getUserSessionError prevents this from turning into an infinite loop.
		// if the dispatch(getUSerSession...) lambda call fails, then getUserSessionError will be the result string message
		if (!gettingUserSession && sessionId && !getUserSessionError && !isPreview && eventId && channel && eventUuid) {
			const storedSession = getStorageItem(`ses.${sessionId}`);
			if (storedSession) {
				dispatch(setUserSession(storedSession));
			} else {
				if (!loadingValidSessions || !registrationOn) { // we need to wait to make sure we have passcode lists
					dispatch(getUserSession(
						sessionId,
						eventId,
						channel,
						blProfileUserToken,
						registrationId ?? undefined,
						registeredLanguage ?? undefined,
						getStoredReferrer(eventUuid) ?? undefined,
						Array.from(new Set(validPasscodeLists ?? [])),
					));
				}
			}
		}
		return () => {
			// remove user session from redux so if we go to a new session we dont have a stale user_session_uuid 
			dispatch(resetUserSession());
		};
	}, [
		gettingUserSession,
		sessionId,
		dispatch,
		blProfileUserToken,
		getUserSessionError,
		registrationId,
		isPreview,
		channel,
		eventId,
		eventUuid,
		registrationOn,
		registeredLanguage,
		validPasscodeLists,
		loadingValidSessions,
	]);

	const shouldBlockSessionPageView = false; // pulled out into a named variable to give context about what this boolean is controlling 
	useTrackPageView(shouldBlockSessionPageView, language);

	const tabGroupMap = useMemo(() => {
		return session?.module_grouping?.reduce((acc, curr) => {
			acc[curr.uuid] = curr;
			return acc;
		}, {} as { [key: string]: PageModuleGroupModules; });
	}, [session]);

	const groupedNavButtons = useMemo(() => {
		return session?.module_grouping?.map(({ name, uuid, icon, is_on, modules, type }) => {

			// Breakout Rooms don't have modules.length so add check for BRs here too
			if (!is_on || (!modules.length && (name as TranslateString).base !== ModuleGroups.BreakoutRooms)) return null;

			//If there is no content in the module don't make the tab
			const _modules = session.modules.filter(module => module?.id && modules.includes(module.id) && module.is_on);

			function notSupposedToHaveModules(type: PageModuleType) {
				switch (type) {
					case PageModuleType.description:
					case PageModuleType.questions:
					case PageModuleType.blank:
					case PageModuleType.embed_widget: return true;
					default: return false;
				}
			}
			const hasContent = _modules.some(_module => notSupposedToHaveModules(_module.type) || !!_module.modules?.length || !!_module.content_modules?.length);

			if (!hasContent && type !== "breakout") return null;

			let _name: string;
			//Check to see if "_name" is a Translate string object, if so return the translated value, if not, then it is already a string. Use static translation if available or return the string
			const translateName = name as TranslateString;
			if (typeof translateName === "object") {
				_name = t(`${translationNamespace}:tabName.${translateName.base}`, translateName[language] as string ?? translateName.base);
			} else {
				_name = t(name as string) ?? name;
			}

			return (
				<li key={uuid}>
					<input
						type="radio"
						name="tab-navigation"
						id={`session-${uuid}`}
						value={uuid}
						onChange={handleTabChange}
						checked={uuid === selectedTabUuid}
					/>
					<label htmlFor={`session-${uuid}`}>
						<Icon name={icon || ""} size={16} color={classNames({ noIcon: !showIcons })} />
						<span>{_name}</span>
					</label>
				</li>
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		session?.module_grouping,
		session?.modules,
		handleTabChange,
		selectedTabUuid,
		showIcons,
		translationNamespace,
		t,
		language,
		translationUpdates // need to force re-render when translationUpdates has fired
	]);

	const renderModulesByGroup = () => {
		if (!selectedTabUuid || !session || !tabGroupMap) return <></>;
		const templateClassName = getTemplateClassName(template);
		return <RenderSessionModules moduleGroup={tabGroupMap[selectedTabUuid]} session={session} template={templateClassName} preview={previewSession ? true : false} />;
	};

	const renderAllModules = () => {
		if (!session || !tabGroupMap) return <></>;
		const templateClassName = getTemplateClassName(template);
		return (
			<>
				{session?.module_grouping?.map(group => <RenderSessionModules key={group.uuid} moduleGroup={group} session={session} template={templateClassName} preview={previewSession ? true : false} />)}
			</>
		);
	};

	const feedbackSurvey = () => {
		if (session?.enable_feedback_survey && sessionStarted) {
			return (
				<FeedbackSurveyAlert
					onClick={rating => {
						setFeedbackRating(rating);
						setShowFeedbackSurveyModal(true);
					}}
					rating={sessionUserSurveyRatings[session.uuid]}
					show={showFeedbackSurveyAlert}
					setShow={setShowFeedbackSurveyAlert}
					layout={layout}
				/>
			);
		}
	};

	// the tab-navigation component is the session navigation panel,
	// not to be confused with a tab-header component.  
	const renderContent = () => {
		switch (layout) {
			case SessionTabsLayoutTypes.Vertical:
				return (
					<>
						<ul className="tab-navigation">
							{groupedNavButtons}
						</ul>
						<div className="page-container-body">
							{feedbackSurvey()}
							{renderModulesByGroup()}
						</div>
					</>
				);
			case SessionTabsLayoutTypes.NoTabs:
				return (
					<div className="page-container-body">
						{feedbackSurvey()}
						{renderAllModules()}
					</div>
				);
			default: // Horizontal
				return (
					<>
						<Suspense fallback="">
							<Sticky
								top={session && session.languages?.length > 1 ? '70px' : '0px'}
								renderStickyElements={() => (
									<ul className="tab-navigation">
										{groupedNavButtons}
									</ul>
								)}
							/>
						</Suspense>
						{feedbackSurvey()}
						<div className="page-container-body">
							{renderModulesByGroup()}
						</div>
					</>
				);
		}
	};

	const handleChatClose = useCallback((chatClosed: boolean) => {
		setChatClosed(chatClosed);
	}, []);

	const { Track } = useTracking({ source_type: ActionSource.Session, source_id: session?.session });

	const ninetySeconds = 1000 * 60 * 1.5;
	const fiveMinutes = 1000 * 60 * 5;
	useTrackEndSessionTimes({ interval: ninetySeconds, runOneInterval: true });
	useTrackEndSessionTimes({ interval: fiveMinutes, beginAfter: ninetySeconds, sessionId });

	const profileRefDesktop = useRef(null);
	const hasNavigation = eventBundle?.homepage;

	// if single session event and no langing page and on session page and not mobile, make z-index 0
	const isSingleSession = eventBundle?.sessions?.length === 1;
	const match = matchPath(history.location.pathname, {
		path: [
			DebugDefaultRouteMap.Landing,
			DefaultRouteMap.Landing,
		],
		exact: true
	});

	const setChatUnderVideo = match && isSingleSession && !hasNavigation && !isMobile && !isLessThan640;

	// useEffect(() => {
	// 	if (isPreview) return;

	// 	const handleOrientationChange = () => {
	// 		if (window.innerHeight > window.innerWidth) {
	// 			setSimulateFullscreen(true);
	// 		} else {
	// 			setSimulateFullscreen(false);
	// 		}
	// 	};

	// 	console.log("Added orientation change event...");
	// 	window.addEventListener('orientationchange', handleOrientationChange, true);

	// 	return () => {
	// 		window.removeEventListener('orientationchange', handleOrientationChange);
	// 	};
	// }, [isPreview]);

	// if backup url, do not display the session page at all otherwise it temp flashes the session page
	if ((session?.backup_url && session?.backup_url_enabled)) {
		return null;
	}

	//no redirect if this is the preview
	if (eventBundle?.registration_on && !registrationId && !isPreview) {
		return <Redirect to={`/${eventPath(eventName, uuid)}/${language}/registration`} />;
	}

	if (loadingEventBundle || !session) {
		return (
			<div className={classNames('session', getTemplateClassName(template))}>
				<div className={"session-main-content"}>
					<div
						className={classNames("modules-container", layoutClassName, backgroundColor)}
						style={backgroundImg}
					>
					</div>
				</div>
			</div>
		);
	}

	//do not redirect if this is the preview
	//user has invalid validation OR has not yet registered and registration is on, so redirect to registration page
	if (!hasEventAccess(eventBundle, validSessions, finishedRegistering, userVerified, firesidesHostSessions) && !isPreview) {
		return <Redirect to={`/${eventPath(eventName, uuid)}/${language}/registration`} />;
	}

	if (eventBundle?.registration_on && ticketingStep && !hasTicket && !isPreview) {
		return <Redirect to={`/${eventPath(eventName, uuid)}/${language}`} />;
	}

	const previewNavigation = previewBundle?.homepage;
	const isBreakout = session?.session_type === SessionTypesEnum.breakoutRooms;
	const isIFrameBroadcast = session.session_type === SessionTypesEnum.broadcast && session.broadcast_type === EBroadcastTypes.embed;
	const mobileChat = !isBreakout
		&& (editorSize === EditorSizes.mobile
			|| isLessThan640
			|| isFiresideTablet
			|| (isFireside && editorSize && editorSize !== EditorSizes.desktop)
			|| (isIFrameBroadcast && isLessThan1024)
			|| (isIFrameBroadcast && editorSize && editorSize !== EditorSizes.desktop));

	const tabletChat = attendeeInPersonModeEnabled
		&& !isBreakout
		&& (editorSize === EditorSizes.tablet
			|| isLessThan1024
			|| isFiresideTablet
			|| (isFireside && editorSize && editorSize !== EditorSizes.desktop)
			|| (isIFrameBroadcast && isLessThan1024)
			|| (isIFrameBroadcast && editorSize && editorSize !== EditorSizes.desktop));


	if (previewSession && previewBundle) {
		// display the profile bubble if there is no homepage but
		// registration is on or the session has more than 1 language and its desktop preview
		const noHomepageShowProfileBubble = !previewNavigation && (previewBundle?.registration_on || (previewBundle?.sessions[0]?.languages?.length ?? 0) > 1) && editorSize === EditorSizes.desktop;

		return (
			<div className={classNames('session', getTemplateClassName(template), { 'breakout-session': session.session_type === SessionTypesEnum.breakoutRooms })}>
				{
					previewNavigation && <MarketingNav template={getTemplateClassName(template)} previewHide={previewLessThan1024} logo={logo ?? undefined} previewLanguage={previewLanguage} />
				}
				{noHomepageShowProfileBubble && adminUser && adminUser?.active_channel && previewBundle && (
					<aside className="profile-details-actions single-session">
						<Suspense fallback="">
							<ProfileDetailsDropdown
								profile={getMockBlProfile(adminUser)}
								channel={adminUser?.active_channel}
								eventBundle={previewBundle}
								template={getTemplateClassName(template)}
								isEditor={!!workingEvent}
							/>
						</Suspense>
					</aside>
				)}
				<div
					className={classNames("session-main-content", {
						"single-session": previewBundle ? !previewNavigation : !hasNavigation,
						'fireside-session': isFireside,
						"attendee-in-person-enabled": attendeeInPersonModeEnabled,
						'chat_enabled': session.session_chat_enabled,
					})}
				>
					<SessionDocumentHead session={session} />
					{session?.session_type === SessionTypesEnum.breakoutRooms ? (
						<div style={{ maxHeight: '460px' }}>
							<MainEventBannerBreakout isEditor={false} session={session} template={getTemplateClassName(template)} />
						</div>
					) : (
						<>
							<SessionHeader
								session={session}
								eventBundle={previewBundle}
								styleOverrides={{}}
								previewLanguage={previewLanguage}
								hideNavItems={editorSize === 'desktop'}
								editorSize={editorSize}
								isEditor={!!workingEvent}
								ref={sessionHeaderRef}
							/>
							<SessionStream
								languageProp={language}
								session={session}
								eventBundle={previewBundle}
								playback={playbackUrls[session.uuid]}
								editorSize={editorSize}
								sessionHeaderRef={sessionHeaderRef}
							/>
						</>
					)}
					{(tabletChat || mobileChat) && session.session_chat_enabled && (
						<Suspense fallback="">
							<LiveChat
								session={session}
								loading={!!loadingEventBundle}
								language={language}
								registrationOn={!!previewBundle?.registration_on}
								blProfileUser={blProfileUser as BlProfile}
								template={getTemplateClassName(previewBundle.template.name)}
								forceOverlay={false}
								mobileOnly={true}
								isEditor={true}
								eventBundle={previewBundle}
								chatClosed={chatClosed}
								onChatClose={handleChatClose}
							/>
						</Suspense>
					)}
					<OptionalComponent display={!attendeeInPersonModeEnabled}>
						<Suspense fallback="">
							<FiresideHostControls session={session} template={getTemplateClassName(template)} admin />
						</Suspense>
					</OptionalComponent>
					<div
						className={classNames("modules-container", layoutClassName, backgroundColor, { ["display-language-dropdown"]: displaySessionPageLanguageToggle })}
						style={backgroundImg}
					>
						<OptionalComponent display={displaySessionPageLanguageToggle && !isLessThan640}>
							<div className="language-dropdown-container">
								<LanguageDropdown languages={session.languages as LanguagesAbbr[]} currentLanguageCode={language} useISOLangCodes onLanguageChange={handleLanguageChange} isNoScroll={false} animateDropdown />
							</div>
						</OptionalComponent>
						{renderContent()}
					</div>
					{(displayFooter && event?.homepage?.footer) ? (
						<Suspense fallback="">
							<Footer
								footer={event.homepage.footer}
								template={getTemplateClassName(template)}
							/>
						</Suspense>
					) : null}
				</div>
			</div>
		);
	}
	// display the profile bubble if there is no homepage but
	// registration is on or the session has more than 1 language
	const noHomepageShowProfileBubble = !hasNavigation && (event?.registration_on || (event?.sessions[0]?.languages?.length ?? 0) > 1);

	return eventBundle ? (
		<div className={classNames('session', getTemplateClassName(template), {
			'breakout-session': session.session_type === SessionTypesEnum.breakoutRooms,
			'fireside-session': session.session_type === SessionTypesEnum.fireside,
			"attendee-in-person-enabled": attendeeInPersonModeEnabled,
		})}
		>
			<Track>
				{
					// single session events can not have a homepage
					hasNavigation && <ErrorBoundary uniqueLabel="Session marketing nav"><MarketingNav template={getTemplateClassName(template)} logo={logo ?? undefined} /></ErrorBoundary>
				}
				{noHomepageShowProfileBubble && channel && event && (
					<aside
						ref={profileRefDesktop}
						className={classNames('profile-details-actions', 'single-session', { 'set-chat-under-video': setChatUnderVideo })}
					>
						<Suspense fallback="">
							<ProfileDetailsDropdown
								profile={(workingEvent && adminUser) ? getMockBlProfile(adminUser) : blProfileUser}
								channel={channel}
								eventBundle={event}
								template={getTemplateClassName(template)}
								isEditor={!!workingEvent}
							/>
						</Suspense>
					</aside>
				)}
				<div
					className={classNames("session-main-content", {
						'single-session': !eventBundle.homepage,
						'fireside-session': isFireside,
						"attendee-in-person-enabled": attendeeInPersonModeEnabled,
						'chat_enabled': session.session_chat_enabled,
					})}
				>
					<SessionDocumentHead session={session} />
					{session?.session_type === SessionTypesEnum.breakoutRooms ? (
						<div style={{ maxHeight: '460px' }}>
							<MainEventBannerBreakout isEditor={false} session={session} template={getTemplateClassName(template)} />
						</div>
					) : (
						<div className={classNames('session-header-and-stream', { 'chat-open': !chatClosed })}>
							<SessionHeader
								session={session}
								eventBundle={eventBundle}
								styleOverrides={{}}
								previewLanguage={previewLanguage}
								hideNavItems={!isLessThan1024}
								editorSize={editorSize}
								isEditor={!!workingEvent}
								ref={sessionHeaderRef}
							/>
							<ErrorBoundary uniqueLabel="Session stream">
								<SessionStream
									languageProp={language}
									session={session}
									eventBundle={eventBundle}
									playback={playbackUrls?.[`${session.uuid}-${single_stream ? session.default_language : language}`]}
									secondaryVideos={secondaryVideos}
									sessionHeaderRef={sessionHeaderRef}
								/>
							</ErrorBoundary>
						</div>
					)}
					{(tabletChat || mobileChat) && session.session_chat_enabled && (
						<Suspense fallback="">
							<ErrorBoundary uniqueLabel="Mobile live chat">
								<LiveChat
									session={session}
									loading={!!loadingEventBundle}
									language={language}
									registrationOn={!!eventBundle?.registration_on}
									blProfileUser={blProfileUser as BlProfile}
									template={getTemplateClassName(eventBundle.template.name)}
									forceOverlay={false}
									mobileOnly={true}
									chatClosed={chatClosed}
									onChatClose={handleChatClose}
									eventBundle={eventBundle}
								/>
							</ErrorBoundary>
						</Suspense>
					)}
					<OptionalComponent display={!attendeeInPersonModeEnabled}>
						<Suspense fallback="">
							<FiresideHostControls session={session} template={getTemplateClassName(template)} />
						</Suspense>
					</OptionalComponent>

					<div
						className={classNames("modules-container", layoutClassName, backgroundColor, { "attendee-in-person-enabled": attendeeInPersonModeEnabled, ["display-language-dropdown"]: displaySessionPageLanguageToggle })}
						style={backgroundImg}
					>
						<OptionalComponent display={displaySessionPageLanguageToggle && !isLessThan640}>
							<div className="language-dropdown-container">
								<LanguageDropdown languages={session.languages as LanguagesAbbr[]} currentLanguageCode={language} useISOLangCodes onLanguageChange={handleLanguageChange} isNoScroll={false} animateDropdown />
							</div>
						</OptionalComponent>
						{renderContent()}
					</div>
					{(displayFooter && event?.homepage?.footer) ? (
						<Suspense fallback="">
							<Footer
								footer={event.homepage.footer}
								template={getTemplateClassName(template)}
							/>
						</Suspense>
					) : null}

					<Suspense fallback="">
						{((showFeedbackSurveyModal && !!sessionUserSurveyRatings[session.uuid]) || !!surveyId) &&
							<UserSurveyModal
								setFeedbackRating={setFeedbackRating}
								setShowFeedbackSurveyAlert={setShowFeedbackSurveyAlert}
								onClose={() => {
									setFeedbackRating(0);
									setSurveyId(null);
									setShowFeedbackSurveyModal(false);
								}}
								template={getTemplateClassName(template)}
								feedbackRating={sessionUserSurveyRatings[session.uuid] || 0}
								surveyID={surveyId || null}
								language={language}
								sessionUuid={sessionUuid}
							/>}
					</Suspense>
				</div>
			</Track >
		</div >
	) : previewBundle ? (
		<div className={classNames('session', getTemplateClassName(template), { 'breakout-session': session.session_type === SessionTypesEnum.breakoutRooms, 'fireside-session': session.session_type === SessionTypesEnum.fireside })}>
			<div
				className={classNames("session-main-content", {
					'single-session': !previewBundle?.homepage,
					'fireside-session': isFireside,
					'attendee-in-person-enabled': attendeeInPersonModeEnabled,
					'chat_enabled': session.session_chat_enabled,
				})}
			>
				{session.session_type === SessionTypesEnum.breakoutRooms ? (
					<div style={{ maxHeight: '460px' }}>
						<MainEventBannerBreakout session={session} template={getTemplateClassName(template)} />
					</div>
				) : (
					<>
						<SessionHeader
							styleOverrides={{}}
							session={session}
							eventBundle={previewBundle}
							previewLanguage={previewLanguage}
							hideNavItems={!isLessThan1024}
							editorSize={editorSize}
							isEditor={!!workingEvent}
						/>
						<SessionStream
							languageProp={language}
							session={session}
							editorSize={editorSize}
							eventBundle={previewBundle}
							playback={playbackUrls[`${session.uuid}-${single_stream ? session.default_language : language}`]}
							secondaryVideos={secondaryVideos}
							isEditor
						/>
						{(tabletChat || mobileChat) && session.session_chat_enabled && (
							<Suspense fallback="">
								<LiveChat
									session={session}
									loading={!!loadingEventBundle}
									language={language}
									registrationOn={!!previewBundle?.registration_on}
									blProfileUser={blProfileUser as BlProfile}
									template={getTemplateClassName(previewBundle.template.name)}
									forceOverlay={false}
									mobileOnly={true}
									isEditor={true}
									eventBundle={previewBundle}
									chatClosed={chatClosed}
									onChatClose={handleChatClose}
								/>
							</Suspense>
						)}
						<OptionalComponent display={!attendeeInPersonModeEnabled}>
							<Suspense fallback="">
								<FiresideHostControls session={session} template={getTemplateClassName(template)} admin />
							</Suspense>
						</OptionalComponent>
					</>
				)}
				<div
					className={classNames("modules-container", layoutClassName, backgroundColor, { ["display-language-dropdown"]: displaySessionPageLanguageToggle })}
					style={backgroundImg}
				>
					<OptionalComponent display={displaySessionPageLanguageToggle && !isLessThan640}>
						<div className="language-dropdown-container">
							<LanguageDropdown languages={session.languages as LanguagesAbbr[]} currentLanguageCode={language} useISOLangCodes onLanguageChange={handleLanguageChange} isNoScroll={false} animateDropdown />
						</div>
					</OptionalComponent>
					{renderContent()}
				</div>
				{
					(displayFooter && event?.homepage?.footer) ? (
						<Suspense fallback="">
							<Footer
								footer={event.homepage.footer}
								template={getTemplateClassName(template)}
							/>
						</Suspense>
					) : null
				}
			</div >
			<Suspense fallback="">
				<SessionPreviewModal open={previewMode === EPreviewTypes.Session} singleSession={workingEvent?.sessions.length === 1} />
			</Suspense>
		</div >
	) : null;
};

export default SessionView;

